import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  UtensilsCrossed,
  Eye,
  EyeOff,
  AlertCircle,
  Loader,
  ChefHat,
  Mail,
  Lock,
} from "lucide-react";
import { signInWithEmailAndPassword, sendPasswordResetEmail } from "firebase/auth";
import { auth } from "../firebase";
import { getApiUrl } from "../config/api";
import { signInWithGoogle } from "../utils/oauthAuth";

const Login = () => {
  const [formData, setFormData] = useState({
    emailOrUsername: "",
    password: "",
  });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState("");
  const [forgotPasswordLoading, setForgotPasswordLoading] = useState(false);
  const [forgotPasswordSuccess, setForgotPasswordSuccess] = useState(false);
  const [stats, setStats] = useState({
    restaurants: 0,
    users: 0,
    reviews: 0,
  });
  const [statsLoaded, setStatsLoaded] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  const successMessage = location.state?.message;

  // Fetch statistics on component mount
  useEffect(() => {
    const fetchStats = async () => {
      try {
        const apiUrl = getApiUrl("/stats");
        
        const response = await fetch(apiUrl);
        
        // Check if response is actually JSON (not HTML)
        const contentType = response.headers.get("content-type");
        if (!contentType || !contentType.includes("application/json")) {
          if (process.env.NODE_ENV === 'development') {
            await response.text(); // Consume the response body
            console.error("Stats endpoint returned non-JSON response:", {
              status: response.status,
              contentType: contentType,
              url: apiUrl,
            });
          }
          setStatsLoaded(true);
          return;
        }
        
        if (response.ok) {
          const data = await response.json();
          setStats(data);
          setStatsLoaded(true);
        } else {
          if (process.env.NODE_ENV === 'development') {
            console.error("Stats endpoint returned error:", response.status, response.statusText);
          }
          setStatsLoaded(true);
        }
      } catch (error) {
        if (process.env.NODE_ENV === 'development') {
          console.error("Failed to fetch statistics:", error);
        }
        setStatsLoaded(true); // Set to true even on error to avoid infinite loading
      }
    };
    fetchStats();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    setError("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      let loginEmail = formData.emailOrUsername;

      // If username (no @), look up email from backend
      if (!loginEmail.includes("@")) {
        // SECURITY: Don't log sensitive information
        if (process.env.NODE_ENV === 'development') {
          console.log("Username provided, looking up email...");
        }
        const userResponse = await fetch(
          getApiUrl(`/users/${formData.emailOrUsername}`)
        );

        if (!userResponse.ok) {
          // Don't reveal if username exists or not for security
          throw new Error("Invalid email/username or password");
        }

        const userData = await userResponse.json();
        loginEmail = userData.email;
        // SECURITY: Don't log email addresses
      }

      // Authenticate with Firebase
      // SECURITY: Don't log email addresses
      let userCredential;
      try {
        userCredential = await signInWithEmailAndPassword(
          auth,
          loginEmail,
          formData.password
        );
        // SECURITY: Don't log UIDs or sensitive data
      } catch (firebaseError) {
        // SECURITY: Log errors only in development, don't expose details
        if (process.env.NODE_ENV === 'development') {
          console.error("Firebase authentication error:", firebaseError.code);
        }
        
        // Firebase error codes
        if (firebaseError.code === "auth/invalid-credential") {
          // SECURITY: Don't reveal whether user exists or not
          throw new Error("Invalid email/username or password");
        } else if (firebaseError.code === "auth/user-not-found") {
          throw new Error("Invalid email/username or password");
        } else if (firebaseError.code === "auth/wrong-password") {
          throw new Error("Invalid email/username or password");
        } else if (firebaseError.code === "auth/network-request-failed") {
          throw new Error("Network error. Please check your connection and try again.");
        } else if (firebaseError.code === "auth/too-many-requests") {
          throw new Error("Too many failed attempts. Please try again later.");
        } else if (firebaseError.code === "auth/user-disabled") {
          throw new Error("This account has been disabled. Please contact support.");
        } else {
          throw new Error(firebaseError.message || "Authentication failed. Please try again.");
        }
      }

      // Fetch user data from backend with authentication token
      const apiUrl = getApiUrl(`/users/auth/${userCredential.user.uid}`);
      const token = await userCredential.user.getIdToken();
      
      const response = await fetch(apiUrl, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        // SECURITY: Don't expose error details
        throw new Error("Failed to fetch user data");
      }

      const userData = await response.json();
      // SECURITY: Don't log user data

      localStorage.setItem("userData", JSON.stringify(userData));

      navigate("/map");
    } catch (err) {
      // SECURITY: Log errors only in development, don't expose stack traces
      if (process.env.NODE_ENV === 'development') {
        console.error("Login error:", err.message);
      }
      
      let errorMessage = err.message;
      
      // Handle specific error cases
      if (err.message === "Invalid email/username or password") {
        errorMessage = "Invalid email/username or password";
      } else if (err.code === "auth/invalid-credential" || 
                 err.code === "auth/user-not-found" ||
                 err.code === "auth/wrong-password") {
        errorMessage = "Invalid email/username or password";
      } else if (err.code === "auth/network-request-failed") {
        errorMessage = "Network error. Please check your connection and try again.";
      } else if (err.code === "auth/too-many-requests") {
        errorMessage = "Too many failed attempts. Please try again later.";
      } else if (err.message.includes("Failed to fetch") || err.message.includes("NetworkError")) {
        errorMessage = "Cannot connect to server. Please check your internet connection.";
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setForgotPasswordLoading(true);
    setError("");
    setForgotPasswordSuccess(false);

    try {
      let resetEmail = forgotPasswordEmail.trim();

      if (!resetEmail) {
        throw new Error("Please enter your email or username");
      }

      // If username (no @), look up email from backend
      if (!resetEmail.includes("@")) {
        // SECURITY: Don't log sensitive information
        const userResponse = await fetch(
          getApiUrl(`/users/${resetEmail}`)
        );

        if (!userResponse.ok) {
          // Don't reveal if username exists for security
          throw new Error("If an account exists with this email/username, a password reset link has been sent.");
        }

        const userData = await userResponse.json();
        resetEmail = userData.email;
        // SECURITY: Don't log email addresses
      }

      // Send password reset email
      // SECURITY: Don't log email addresses
      await sendPasswordResetEmail(auth, resetEmail);
      
      setForgotPasswordSuccess(true);
      setForgotPasswordEmail("");
      // SECURITY: Don't log success with sensitive data
    } catch (err) {
      // SECURITY: Log errors only in development
      if (process.env.NODE_ENV === 'development') {
        // Error handling - no console log needed
      }
      let errorMessage = err.message;
      
      // Handle Firebase errors
      if (err.code === "auth/user-not-found") {
        // Don't reveal if user exists for security
        errorMessage = "If an account exists with this email/username, a password reset link has been sent.";
        setForgotPasswordSuccess(true); // Show success message even if user doesn't exist (security best practice)
      } else if (err.code === "auth/invalid-email") {
        errorMessage = "Please enter a valid email address.";
      } else if (err.code === "auth/network-request-failed") {
        errorMessage = "Network error. Please check your connection and try again.";
      } else if (err.message.includes("Failed to fetch")) {
        errorMessage = "Cannot connect to server. Please check your internet connection.";
      }
      
      setError(errorMessage);
    } finally {
      setForgotPasswordLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setLoading(true);
    setError("");

    try {
      const result = await signInWithGoogle();
      if (result.success) {
        navigate("/map");
      }
    } catch (err) {
      let errorMessage = err.message;
      if (err.code === "auth/popup-closed-by-user") {
        errorMessage = "Sign-in popup was closed. Please try again.";
      } else if (err.code === "auth/popup-blocked") {
        errorMessage = "Popup was blocked. Please allow popups for this site.";
      } else if (err.code === "auth/account-exists-with-different-credential") {
        errorMessage = "An account already exists with this email. Please sign in with your original method.";
      } else if (err.code === "auth/unauthorized-domain") {
        errorMessage = "This domain is not authorized for OAuth. Please contact the administrator to add this domain to Firebase authorized domains.";
      }
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };


  return (
    <div className="min-h-screen flex">
      {/* Left Side - Image & Branding */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.8 }}
        className="hidden lg:flex lg:w-1/2 relative overflow-hidden"
      >
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(/food-hero.jpg)` }}
        />
        <div className="absolute inset-0 bg-gradient-to-br from-orange-500/80 via-orange-600/70 to-orange-700/80" />
        
        {/* Floating Elements */}
        <motion.div
          animate={{ y: [-10, 10, -10] }}
          transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
          className="absolute top-20 right-20"
        >
          <ChefHat className="w-16 h-16 text-white/30" />
        </motion.div>
        <motion.div
          animate={{ y: [10, -10, 10] }}
          transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
          className="absolute bottom-32 left-16"
        >
          <UtensilsCrossed className="w-20 h-20 text-white/20" />
        </motion.div>

        {/* Content */}
        <div className="relative z-10 flex flex-col justify-center p-12 text-white">
          <motion.div
            initial={{ x: -50, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            transition={{ delay: 0.3, duration: 0.6 }}
          >
            <h1 className="text-6xl font-bold mb-4">Foodify</h1>
            <p className="text-xl opacity-90 mb-8 max-w-md leading-relaxed">
              Discover, share, and connect with food lovers around the world.
              Your culinary journey starts here.
            </p>
            
            {/* Stats */}
            {statsLoaded && (
              <div className="flex gap-8 mt-12">
                {[
                  { number: stats.restaurants, label: "Restaurants" },
                  { number: stats.users, label: "Food Lovers" },
                  { number: stats.reviews, label: "Reviews" },
                ].map((stat, index) => (
                  <motion.div
                    key={stat.label}
                    initial={{ y: 20, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ delay: 0.5 + index * 0.1 }}
                    className="text-center"
                  >
                    <div className="text-3xl font-bold">
                      {Math.floor(stat.number / 10) * 10}+
                    </div>
                    <div className="text-sm opacity-80">{stat.label}</div>
                  </motion.div>
                ))}
              </div>
            )}
          </motion.div>
        </div>
      </motion.div>

      {/* Right Side - Auth Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8 bg-[#fef8f3]">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="w-full max-w-md"
        >
          {/* Mobile Logo */}
          <div className="lg:hidden text-center mb-8">
            <h1 className="text-4xl font-bold text-orange-500">Foodify</h1>
            <p className="text-gray-500 mt-2">Your culinary journey awaits</p>
          </div>

          {/* Form */}
          <AnimatePresence mode="wait">
            <motion.form
              key="login"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
              transition={{ duration: 0.3 }}
              onSubmit={handleSubmit}
              className="space-y-5"
            >
              <div className="mb-6">
                <h2 className="text-3xl font-semibold text-gray-900">
                  Welcome back!
                </h2>
                <p className="text-gray-500 mt-2">
                  Enter your credentials to access your account
                </p>
              </div>

              {/* Success Message */}
              <AnimatePresence>
                {successMessage && (
                  <motion.div
                    initial={{ opacity: 0, x: -20, scale: 0.95 }}
                    animate={{ opacity: 1, x: 0, scale: 1 }}
                    exit={{ opacity: 0, x: 20, scale: 0.95 }}
                    transition={{ duration: 0.3 }}
                    className="mb-6 p-4 bg-green-50 border-l-4 border-green-500 rounded-r-lg"
                  >
                    <div className="flex items-center">
                      <svg className="h-5 w-5 text-green-500 mr-3" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      <p className="text-sm text-green-800 font-medium">{successMessage}</p>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Error Message */}
              <AnimatePresence>
                {error && (
                  <motion.div
                    initial={{ opacity: 0, x: -20, scale: 0.95 }}
                    animate={{ opacity: 1, x: 0, scale: 1 }}
                    exit={{ opacity: 0, x: 20, scale: 0.95 }}
                    transition={{ duration: 0.3 }}
                    className="mb-6 p-4 bg-red-50 border-l-4 border-red-500 rounded-r-lg"
                  >
                    <div className="flex items-start">
                      <AlertCircle className="w-5 h-5 text-red-500 mt-0.5 mr-3 flex-shrink-0" />
                      <p className="text-sm text-red-800">{error}</p>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Email Field */}
              <div>
                <label htmlFor="emailOrUsername" className="block text-sm font-medium text-gray-900 mb-2">
                  Email or Username
                </label>
                <div className="relative mt-2">
                  <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                  <input
                    id="emailOrUsername"
                    name="emailOrUsername"
                    type="text"
                    placeholder="hello@foodify.com"
                    value={formData.emailOrUsername}
                    onChange={handleChange}
                    required
                    className="w-full pl-12 pr-4 h-12 rounded-lg bg-gray-100 border-none focus:ring-2 focus:ring-orange-500/30 transition-all"
                  />
                </div>
              </div>

              {/* Password Field */}
              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-900 mb-2">
                  Password
                </label>
                <div className="relative mt-2">
                  <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                  <input
                    id="password"
                    name="password"
                    type={showPassword ? "text" : "password"}
                    placeholder="••••••••"
                    value={formData.password}
                    onChange={handleChange}
                    required
                    className="w-full pl-12 pr-12 h-12 rounded-lg bg-gray-100 border-none focus:ring-2 focus:ring-orange-500/30 transition-all"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-900 transition-colors"
                  >
                    {showPassword ? (
                      <EyeOff className="w-5 h-5" />
                    ) : (
                      <Eye className="w-5 h-5" />
                    )}
                  </button>
                </div>
              </div>

              {/* Forgot Password */}
              <div className="text-right">
                <button
                  type="button"
                  onClick={() => {
                    setShowForgotPassword(true);
                    setError("");
                    setForgotPasswordSuccess(false);
                  }}
                  className="text-sm text-orange-500 hover:text-orange-600 transition-colors font-medium"
                >
                  Forgot password?
                </button>
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={loading}
                className="w-full h-12 text-base font-semibold bg-gradient-to-r from-orange-500 via-orange-500 to-yellow-500 hover:opacity-90 transition-opacity rounded-lg shadow-[0_8px_30px_-6px_rgba(249,115,22,0.3)] text-white"
              >
                {loading ? (
                  <div className="flex items-center justify-center">
                    <Loader className="w-5 h-5 animate-spin mr-2" />
                    Signing in...
                  </div>
                ) : (
                  "Sign In"
                )}
              </button>

              {/* Divider */}
              <div className="relative my-6">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-200" />
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-4 bg-[#fef8f3] text-gray-500">
                    or continue with
                  </span>
                </div>
              </div>

              {/* Social Login */}
              <div className="flex justify-center">
                <button
                  type="button"
                  onClick={handleGoogleSignIn}
                  disabled={loading}
                  className="w-full h-12 border border-gray-200 rounded-lg hover:bg-gray-100 hover:border-orange-500/30 transition-all flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                    <path
                      fill="currentColor"
                      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                    />
                    <path
                      fill="currentColor"
                      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                    />
                    <path
                      fill="currentColor"
                      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                    />
                    <path
                      fill="currentColor"
                      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                    />
                  </svg>
                  Google
                </button>
              </div>
            </motion.form>
          </AnimatePresence>

          <div className="mt-6 text-center">
            <span className="text-sm text-gray-500">
              Don't have an account?{" "}
              <button
                type="button"
                onClick={() => navigate("/register")}
                className="text-orange-500 hover:underline font-medium"
              >
                Sign up
              </button>
            </span>
          </div>
        </motion.div>
      </div>

      {/* Forgot Password Modal */}
      <AnimatePresence>
        {showForgotPassword && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            onClick={() => {
              setShowForgotPassword(false);
              setForgotPasswordEmail("");
              setError("");
              setForgotPasswordSuccess(false);
            }}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6"
            >
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-semibold text-gray-900">
                  Reset Password
                </h2>
                <button
                  type="button"
                  onClick={() => {
                    setShowForgotPassword(false);
                    setForgotPasswordEmail("");
                    setError("");
                    setForgotPasswordSuccess(false);
                  }}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {forgotPasswordSuccess ? (
                <div className="space-y-4">
                  <div className="p-4 bg-green-50 border-l-4 border-green-500 rounded-r-lg">
                    <div className="flex items-start">
                      <svg className="h-5 w-5 text-green-500 mr-3 mt-0.5" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      <div>
                        <p className="text-sm font-medium text-green-800">
                          Password reset email sent!
                        </p>
                        <p className="text-sm text-green-700 mt-1">
                          Check your email for instructions to reset your password. If you don't see it, check your spam folder.
                        </p>
                      </div>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      setShowForgotPassword(false);
                      setForgotPasswordEmail("");
                      setForgotPasswordSuccess(false);
                    }}
                    className="w-full h-12 bg-gradient-to-r from-orange-500 to-orange-600 text-white font-semibold rounded-lg hover:from-orange-600 hover:to-orange-700 transition-all"
                  >
                    Close
                  </button>
                </div>
              ) : (
                <form onSubmit={handleForgotPassword} className="space-y-4">
                  <p className="text-sm text-gray-600 mb-4">
                    Enter your email or username and we'll send you a link to reset your password.
                  </p>

                  {/* Error Message */}
                  <AnimatePresence>
                    {error && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        exit={{ opacity: 0, height: 0 }}
                        className="p-3 bg-red-50 border-l-4 border-red-500 rounded-r-lg"
                      >
                        <div className="flex items-start">
                          <AlertCircle className="w-5 h-5 text-red-500 mt-0.5 mr-2 flex-shrink-0" />
                          <p className="text-sm text-red-800">{error}</p>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>

                  {/* Email/Username Input */}
                  <div>
                    <label htmlFor="forgotPasswordEmail" className="block text-sm font-medium text-gray-900 mb-2">
                      Email or Username
                    </label>
                    <div className="relative">
                      <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                      <input
                        id="forgotPasswordEmail"
                        type="text"
                        placeholder="Enter your email or username"
                        value={forgotPasswordEmail}
                        onChange={(e) => {
                          setForgotPasswordEmail(e.target.value);
                          setError("");
                        }}
                        required
                        className="w-full pl-12 pr-4 h-12 rounded-lg bg-gray-100 border-none focus:ring-2 focus:ring-orange-500/30 transition-all"
                        autoFocus
                      />
                    </div>
                  </div>

                  {/* Buttons */}
                  <div className="flex gap-3 pt-2">
                    <button
                      type="button"
                      onClick={() => {
                        setShowForgotPassword(false);
                        setForgotPasswordEmail("");
                        setError("");
                        setForgotPasswordSuccess(false);
                      }}
                      className="flex-1 h-12 border border-gray-200 rounded-lg hover:bg-gray-50 transition-all font-medium text-gray-700"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={forgotPasswordLoading}
                      className="flex-1 h-12 bg-gradient-to-r from-orange-500 to-orange-600 text-white font-semibold rounded-lg hover:from-orange-600 hover:to-orange-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {forgotPasswordLoading ? (
                        <div className="flex items-center justify-center">
                          <Loader className="w-5 h-5 animate-spin mr-2" />
                          Sending...
                        </div>
                      ) : (
                        "Send Reset Link"
                      )}
                    </button>
                  </div>
                </form>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Login;
